import { Grid, Box, Typography, Container, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MissingContent from '../../../../components/shared/errorMessages/MissingContent';
import { ChildTileData } from '../../types/CMSTypes';

interface CMSConsumerItemGroupsGridProps {
  childrenData: ChildTileData[];
  marginTop?: number;
  marginBottom?: number;
  hasTitles?: boolean;
  hasButtons?: boolean;
  hasBackgroundText?: boolean;
  spacingValue?: number;
  hasCollectionLinks?: boolean;
}

const CMSConsumerItemGroupsGrid: React.FC<CMSConsumerItemGroupsGridProps> = ({
                                                                               childrenData,
                                                                               marginTop = 5,
                                                                               marginBottom = 5,
                                                                               hasTitles = false,
                                                                               hasButtons = false,
                                                                               hasBackgroundText = true,
                                                                               spacingValue = 6,
                                                                               hasCollectionLinks = false
                                                                             }) => {
  const navigate = useNavigate();

  // Define the specific order and names we want to show for consumer site
  const desiredItemGroups = [
    { name: 'fabric', displayName: 'Fabric', item_group: '005' },
    { name: 'wallpaper', displayName: 'Wallpaper', item_group: '001' }
  ];

  // Filter and sort the children data to match our desired order
  const getFilteredAndSortedData = (): ChildTileData[] => {
    const filteredData: ChildTileData[] = [];

    desiredItemGroups.forEach(desiredGroup => {
      const matchingChild = childrenData.find(child => {
        // Check both category_name and item_group_name for matches
        const categoryNameMatch = child.category_name?.toLowerCase() === desiredGroup.name;
        const itemGroupNameMatch = child.item_group_name?.toLowerCase() === desiredGroup.name;
        const itemGroupMatch = child.item_group === desiredGroup.item_group;

        // Special handling for homeware which might be a "group" type
        const isHomwareGroup = desiredGroup.name === 'homeware' &&
          child.category_name?.toLowerCase() === 'homeware' &&
          child.category_type === 'group';

        return categoryNameMatch || itemGroupNameMatch || itemGroupMatch || isHomwareGroup;
      });

      if (matchingChild) {
        // For homeware group, we need to ensure it has the correct item_group for navigation
        if (desiredGroup.name === 'homeware' && matchingChild.category_type === 'group') {
          // Override the item_group for homeware navigation
          filteredData.push({
            ...matchingChild,
            item_group: '000' // Force homeware to use item_group 000
          });
        } else {
          filteredData.push(matchingChild);
        }
      }
    });

    console.log('Filtered consumer item groups data:', filteredData); // Debug log
    return filteredData;
  };

  const sortedItemGroups = getFilteredAndSortedData();

  // Handle tile click - navigate to item group page
  const handleTileClick = (itemGroup: ChildTileData) => {
    console.log('Clicked consumer item group:', itemGroup); // Debug log

    // Custom pages (e.g. a custom "Fabric" page that replaces the item group)
    // have no item_group; they're served at /:slug
    if (itemGroup.category_type === 'custom') {
      const slug = (itemGroup.display_name || itemGroup.category_name || '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
      navigate(`/${slug}`);
      return;
    }

    // Special handling for homeware group
    if (itemGroup.category_name?.toLowerCase() === 'homeware' && itemGroup.category_type === 'group') {
      console.log('Navigating to homeware: /item_group/000'); // Debug log
      navigate(`/item_group/000`);
      return;
    }

    // Special handling for wallpaper - navigate to specific application
    if (itemGroup.item_group === '001' || itemGroup.category_name?.toLowerCase() === 'wallpaper') {
      console.log('Navigating to wallpaper application'); // Debug log
      navigate('/hertex-wallpaper');
      return;
    }

    // Regular item group navigation
    if (itemGroup.item_group) {
      console.log(`Navigating to: /item_group/${itemGroup.item_group}`); // Debug log
      navigate(`/item_group/${itemGroup.item_group}`);
    } else {
      console.warn('No item_group found for:', itemGroup); // Debug log
    }
  };

  // Show missing content if no data
  if (!sortedItemGroups || sortedItemGroups.length === 0) {
    return <MissingContent sectionName="Item Group Section" />;
  }

  return (
    <Container maxWidth="xl">
      <Grid
        container
        rowSpacing={'16px'}
        columnSpacing={{ xs: 1, sm: 2, md: '16px' }}
        sx={{ mt: marginTop, mb: marginBottom }}
      >
        {sortedItemGroups.map((itemGroup, index) => (
          <Grid key={itemGroup.category_id || index} item xs={12} sm={6} md={spacingValue}>
            <Box
              sx={{
                position: 'relative',
                cursor: 'pointer',
                width: '100%',
                height: { xs: '320px', lg: '510px' },
                minHeight: '100%',
                overflow: 'hidden',
                '&:hover img': {
                  transform: 'scale(1.03)',
                },
              }}
              onClick={() => handleTileClick(itemGroup)}
            >
              {/* Image */}
              <img
                src={itemGroup.alternate_tile_media_link || itemGroup.tile_display_media_single_link }
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease',
                }}
                alt={itemGroup.category_name || 'Item Group'}
              />

              {/* Overlay with text */}
              {hasBackgroundText && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    background:
                      'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.36) 100%)',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: 'center',
                      fontWeight: 'lighter',
                      color: '#fff',
                      fontSize: '40px',
                      textTransform: 'capitalize',
                      mb: { xs: 3, lg: 4 },
                    }}
                  >
                    {itemGroup.item_group_name || itemGroup.category_name}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Title and Button Section */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginTop: '1.5rem',
              }}
            >
              {hasTitles && (
                <Typography variant="h5">
                  {itemGroup.item_group_name || itemGroup.category_name}
                </Typography>
              )}
              {hasButtons && (
                <Button
                  sx={{
                    borderRadius: '1px',
                    padding: '0.5rem 2.5rem',
                    margin: '1.5rem 0rem',
                  }}
                  variant="outlined"
                  onClick={() => handleTileClick(itemGroup)}
                >
                  Explore More
                </Button>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default CMSConsumerItemGroupsGrid;